/**
 * Nashville (America/Chicago) time utilities.
 *
 * The problem: MUI DateTimePicker always renders in the browser's local timezone.
 * To make it display Nashville time regardless of where the user is, we use
 * "Nashville-as-local" Date objects — Dates whose local time components
 * (getHours, getMinutes, etc.) equal the Nashville wall-clock time.
 *
 * Rule:
 *   - Use asNashvilleLocal()  when initializing/setting DateTimePicker state.
 *   - Use nashvilleLocalToISO() when sending dates to the API (converts back to true UTC).
 */

const NASHVILLE_TZ = "America/Chicago";

/** Nashville's UTC offset in ms at a given instant (handles DST automatically). */
function nashvilleOffsetMs(at: Date = new Date()): number {
  const utcStr = at.toLocaleString("en-US", { timeZone: "UTC" });
  const nashvilleStr = at.toLocaleString("en-US", { timeZone: NASHVILLE_TZ });
  return new Date(nashvilleStr).getTime() - new Date(utcStr).getTime();
}

/**
 * Convert a true UTC Date to a "Nashville-as-local" Date.
 * The browser's local time components of the result equal Nashville wall-clock time.
 * Pass the result to DateTimePicker so it displays Nashville time.
 */
export function asNashvilleLocal(d: Date): Date {
  const nashvilleOff = nashvilleOffsetMs(d);
  const localOff = -d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() + nashvilleOff - localOff);
}

/** Get current time as a Nashville-as-local Date. */
export function nowAsNashvilleLocal(): Date {
  return asNashvilleLocal(new Date());
}

/**
 * Convert a "Nashville-as-local" Date (from a DateTimePicker) back to a correct
 * UTC ISO string for backend API calls.
 */
export function nashvilleLocalToISO(d: Date): string {
  const nashvilleOff = nashvilleOffsetMs(d);
  const localOff = -d.getTimezoneOffset() * 60_000;
  return new Date(d.getTime() - nashvilleOff + localOff).toISOString();
}
